import { create } from 'zustand';
import {
  SURVEY_END_PAGE_ID,
  type FormBrandOverride,
  type FormConsentMode,
  type SurveyAudienceMode,
  type SurveyBranchCondition,
  type SurveyBranchOperator,
  type SurveyBranchRule,
  type SurveyContactKey,
  type SurveyDedupeKey,
  type SurveyFieldDefinition,
  type SurveyFieldType,
  type SurveyPageDefinition,
  type SurveyResponseLimit,
} from '@engage/types';

/**
 * Local builder state for SurveyBuilderPage. Holds the multi-page survey being
 * edited — pages (each with fields + branching rules) plus survey-level
 * settings (audience, response limit, dedupe, reminders, consent, brand).
 *
 * Survey-scoped (reset on mount via `initFrom`). Auto-save is driven from the
 * page component using the `dirty` flag. ID/key generation lives here so the
 * builder never collides keys across pages (the validator rejects dupes).
 */

export interface SurveyBuilderState {
  surveyId: string | null;
  name: string;
  slug: string;
  audienceMode: SurveyAudienceMode;
  responseLimit: SurveyResponseLimit;
  dedupeKey: SurveyDedupeKey;
  brandOverride: FormBrandOverride | null;
  consentMode: FormConsentMode | null;
  consentText: string | null;
  reminderOffsets: number[];
  maxReminders: number;

  pages: SurveyPageDefinition[];
  selectedPageId: string | null;
  selectedFieldKey: string | null;
  dirty: boolean;
  /** Monotonic counter feeding generated page/rule ids — kept past any loaded ids. */
  _seq: number;

  initFrom: (
    survey: {
      id: string;
      name: string;
      slug: string;
      audience_mode: SurveyAudienceMode;
      response_limit: SurveyResponseLimit;
      dedupe_key: SurveyDedupeKey;
      brand_override: FormBrandOverride | null;
      consent_mode: FormConsentMode | null;
      consent_text: string | null;
      reminder_offsets: number[];
      max_reminders: number;
    },
    pages: SurveyPageDefinition[]
  ) => void;

  // survey settings
  setName: (name: string) => void;
  setSlug: (slug: string) => void;
  setAudienceMode: (mode: SurveyAudienceMode) => void;
  setResponseLimit: (limit: SurveyResponseLimit) => void;
  setDedupeKey: (key: SurveyDedupeKey) => void;
  setBrandOverride: (next: FormBrandOverride | null) => void;
  setConsent: (mode: FormConsentMode | null, text: string | null) => void;
  setReminders: (offsets: number[], maxReminders: number) => void;

  // page ops
  addPage: () => void;
  removePage: (pageId: string) => void;
  movePage: (pageId: string, direction: 'up' | 'down') => void;
  updatePageLabel: (pageId: string, label: string) => void;
  selectPage: (pageId: string | null) => void;

  // field ops (scoped to a page)
  addField: (pageId: string, type: SurveyFieldType) => void;
  addPresetField: (
    pageId: string,
    preset: { type: SurveyFieldType; key: string; label: string; contact_key?: SurveyContactKey }
  ) => void;
  removeField: (pageId: string, key: string) => void;
  moveField: (pageId: string, key: string, direction: 'up' | 'down') => void;
  updateField: (pageId: string, key: string, patch: Partial<SurveyFieldDefinition>) => void;
  selectField: (key: string | null) => void;

  // branching ops (scoped to a page)
  addRule: (pageId: string) => void;
  removeRule: (pageId: string, ruleId: string) => void;
  updateRule: (pageId: string, ruleId: string, patch: Partial<SurveyBranchRule>) => void;
  addCondition: (pageId: string, ruleId: string) => void;
  removeCondition: (pageId: string, ruleId: string, index: number) => void;
  updateCondition: (
    pageId: string,
    ruleId: string,
    index: number,
    patch: Partial<SurveyBranchCondition>
  ) => void;

  markSaved: () => void;
}

export const useSurveyBuilderStore = create<SurveyBuilderState>((set) => ({
  surveyId: null,
  name: '',
  slug: '',
  audienceMode: 'ANONYMOUS',
  responseLimit: 'MANY',
  dedupeKey: 'NONE',
  brandOverride: null,
  consentMode: null,
  consentText: null,
  reminderOffsets: [24, 72],
  maxReminders: 2,
  pages: [],
  selectedPageId: null,
  selectedFieldKey: null,
  dirty: false,
  _seq: 1,

  initFrom: (survey, pages) =>
    set({
      surveyId: survey.id,
      name: survey.name,
      slug: survey.slug,
      audienceMode: survey.audience_mode,
      responseLimit: survey.response_limit,
      dedupeKey: survey.dedupe_key,
      brandOverride: survey.brand_override,
      consentMode: survey.consent_mode,
      consentText: survey.consent_text,
      reminderOffsets: survey.reminder_offsets ?? [24, 72],
      maxReminders: survey.max_reminders ?? 2,
      pages,
      selectedPageId: pages[0]?.id ?? null,
      selectedFieldKey: null,
      dirty: false,
      _seq: seedSeq(pages),
    }),

  // ── survey settings ───────────────────────────────────────────────────────
  setName: (name) => set({ name, dirty: true }),
  setSlug: (slug) => set({ slug, dirty: true }),
  setAudienceMode: (audienceMode) => set({ audienceMode, dirty: true }),
  setResponseLimit: (responseLimit) => set({ responseLimit, dirty: true }),
  setDedupeKey: (dedupeKey) => set({ dedupeKey, dirty: true }),
  setBrandOverride: (brandOverride) => set({ brandOverride, dirty: true }),
  setConsent: (consentMode, consentText) => set({ consentMode, consentText, dirty: true }),
  setReminders: (reminderOffsets, maxReminders) =>
    set({ reminderOffsets, maxReminders, dirty: true }),

  // ── pages ───────────────────────────────────────────────────────────────────
  addPage: () =>
    set((s) => {
      const id = `page_${s._seq}`;
      const page: SurveyPageDefinition = {
        id,
        label: `Page ${s.pages.length + 1}`,
        fields: [],
        branching_rules: [],
      };
      return { pages: [...s.pages, page], selectedPageId: id, _seq: s._seq + 1, dirty: true };
    }),

  removePage: (pageId) =>
    set((s) => {
      const pages = s.pages.filter((p) => p.id !== pageId);
      // Drop branch rules anywhere that targeted the removed page.
      const cleaned = pages.map((p) => ({
        ...p,
        branching_rules: p.branching_rules.filter((r) => r.goto_page_id !== pageId),
      }));
      const selectedPageId =
        s.selectedPageId === pageId ? (cleaned[0]?.id ?? null) : s.selectedPageId;
      return { pages: cleaned, selectedPageId, selectedFieldKey: null, dirty: true };
    }),

  movePage: (pageId, direction) =>
    set((s) => {
      const idx = s.pages.findIndex((p) => p.id === pageId);
      if (idx === -1) return s;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= s.pages.length) return s;
      const next = [...s.pages];
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      return { pages: next, dirty: true };
    }),

  updatePageLabel: (pageId, label) =>
    set((s) => ({
      pages: s.pages.map((p) => (p.id === pageId ? { ...p, label } : p)),
      dirty: true,
    })),

  selectPage: (selectedPageId) => set({ selectedPageId, selectedFieldKey: null }),

  // ── fields ────────────────────────────────────────────────────────────────
  addField: (pageId, type) =>
    set((s) => {
      const key = nextKey(allKeys(s.pages), slugForType(type));
      const field = newFieldOfType(type, key);
      return {
        pages: patchPage(s.pages, pageId, (p) => ({ ...p, fields: [...p.fields, field] })),
        selectedPageId: pageId,
        selectedFieldKey: key,
        dirty: true,
      };
    }),

  addPresetField: (pageId, preset) =>
    set((s) => {
      const existing = allKeys(s.pages).includes(preset.key);
      // Canonical contact keys must stay exact (the contact-sync worker matches
      // on contact_key), so re-adding just selects the existing one.
      if (existing) return { selectedPageId: pageId, selectedFieldKey: preset.key };
      const base = newFieldOfType(preset.type, preset.key);
      const field = { ...base, label: preset.label, contact_key: preset.contact_key } as SurveyFieldDefinition;
      return {
        pages: patchPage(s.pages, pageId, (p) => ({ ...p, fields: [...p.fields, field] })),
        selectedPageId: pageId,
        selectedFieldKey: preset.key,
        dirty: true,
      };
    }),

  removeField: (pageId, key) =>
    set((s) => ({
      pages: s.pages.map((p) => {
        // Drop the field from its page; scrub any branch condition referencing it.
        const fields = p.id === pageId ? p.fields.filter((f) => f.key !== key) : p.fields;
        const branching_rules = p.branching_rules
          .map((r) => ({ ...r, conditions: r.conditions.filter((c) => c.field_key !== key) }))
          .filter((r) => r.conditions.length > 0);
        return { ...p, fields, branching_rules };
      }),
      selectedFieldKey: s.selectedFieldKey === key ? null : s.selectedFieldKey,
      dirty: true,
    })),

  moveField: (pageId, key, direction) =>
    set((s) => ({
      pages: patchPage(s.pages, pageId, (p) => {
        const idx = p.fields.findIndex((f) => f.key === key);
        if (idx === -1) return p;
        const target = direction === 'up' ? idx - 1 : idx + 1;
        if (target < 0 || target >= p.fields.length) return p;
        const fields = [...p.fields];
        const [moved] = fields.splice(idx, 1);
        fields.splice(target, 0, moved);
        return { ...p, fields };
      }),
      dirty: true,
    })),

  updateField: (pageId, key, patch) =>
    set((s) => ({
      pages: patchPage(s.pages, pageId, (p) => ({
        ...p,
        fields: p.fields.map((f) =>
          f.key === key ? ({ ...f, ...patch } as SurveyFieldDefinition) : f
        ),
      })),
      dirty: true,
    })),

  selectField: (selectedFieldKey) => set({ selectedFieldKey }),

  // ── branching ───────────────────────────────────────────────────────────────
  addRule: (pageId) =>
    set((s) => {
      const rule: SurveyBranchRule = {
        id: `rule_${s._seq}`,
        connector: 'AND',
        conditions: [{ field_key: '', operator: 'equals', value: '' }],
        goto_page_id: SURVEY_END_PAGE_ID,
      };
      return {
        pages: patchPage(s.pages, pageId, (p) => ({
          ...p,
          branching_rules: [...p.branching_rules, rule],
        })),
        _seq: s._seq + 1,
        dirty: true,
      };
    }),

  removeRule: (pageId, ruleId) =>
    set((s) => ({
      pages: patchPage(s.pages, pageId, (p) => ({
        ...p,
        branching_rules: p.branching_rules.filter((r) => r.id !== ruleId),
      })),
      dirty: true,
    })),

  updateRule: (pageId, ruleId, patch) =>
    set((s) => ({
      pages: patchPage(s.pages, pageId, (p) => ({
        ...p,
        branching_rules: p.branching_rules.map((r) =>
          r.id === ruleId ? { ...r, ...patch } : r
        ),
      })),
      dirty: true,
    })),

  addCondition: (pageId, ruleId) =>
    set((s) => ({
      pages: patchRule(s.pages, pageId, ruleId, (r) => ({
        ...r,
        conditions: [...r.conditions, { field_key: '', operator: 'equals', value: '' }],
      })),
      dirty: true,
    })),

  removeCondition: (pageId, ruleId, index) =>
    set((s) => ({
      pages: patchRule(s.pages, pageId, ruleId, (r) => ({
        ...r,
        // Always keep at least one condition row (the validator requires >= 1).
        conditions:
          r.conditions.length <= 1
            ? r.conditions
            : r.conditions.filter((_, i) => i !== index),
      })),
      dirty: true,
    })),

  updateCondition: (pageId, ruleId, index, patch) =>
    set((s) => ({
      pages: patchRule(s.pages, pageId, ruleId, (r) => ({
        ...r,
        conditions: r.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)),
      })),
      dirty: true,
    })),

  markSaved: () => set({ dirty: false }),
}));

// ─── pure helpers (exported for tests) ──────────────────────────────────────────

export function allKeys(pages: SurveyPageDefinition[]): string[] {
  return pages.flatMap((p) => p.fields.map((f) => f.key));
}

/**
 * Field keys available to a branch rule on `pageId`: every field on that page
 * and all earlier pages (forward-only, inclusive of the current page). Mirrors
 * the server validator's `keysThroughPage`.
 */
export function fieldsAvailableForBranching(
  pages: SurveyPageDefinition[],
  pageId: string
): SurveyFieldDefinition[] {
  const idx = pages.findIndex((p) => p.id === pageId);
  if (idx === -1) return [];
  return pages.slice(0, idx + 1).flatMap((p) => p.fields);
}

/** Page choices a branch rule may jump to: any OTHER page, plus END. */
export function gotoTargets(
  pages: SurveyPageDefinition[],
  pageId: string
): { id: string; label: string }[] {
  const others = pages
    .filter((p) => p.id !== pageId)
    .map((p) => ({ id: p.id, label: p.label }));
  return [...others, { id: SURVEY_END_PAGE_ID, label: 'Finish survey' }];
}

function patchPage(
  pages: SurveyPageDefinition[],
  pageId: string,
  fn: (p: SurveyPageDefinition) => SurveyPageDefinition
): SurveyPageDefinition[] {
  return pages.map((p) => (p.id === pageId ? fn(p) : p));
}

function patchRule(
  pages: SurveyPageDefinition[],
  pageId: string,
  ruleId: string,
  fn: (r: SurveyBranchRule) => SurveyBranchRule
): SurveyPageDefinition[] {
  return patchPage(pages, pageId, (p) => ({
    ...p,
    branching_rules: p.branching_rules.map((r) => (r.id === ruleId ? fn(r) : r)),
  }));
}

function seedSeq(pages: SurveyPageDefinition[]): number {
  let max = 0;
  const scan = (id: string) => {
    const m = /_(\d+)$/.exec(id);
    if (m) max = Math.max(max, Number(m[1]));
  };
  for (const p of pages) {
    scan(p.id);
    for (const r of p.branching_rules) scan(r.id);
  }
  return max + 1;
}

function slugForType(type: SurveyFieldType): string {
  return type.toLowerCase().replace(/_/g, '-');
}

function nextKey(existing: string[], base: string): string {
  let candidate = base.replace(/-/g, '_');
  let suffix = 1;
  while (existing.includes(candidate)) {
    suffix += 1;
    candidate = `${base.replace(/-/g, '_')}_${suffix}`;
  }
  return candidate;
}

export function newFieldOfType(type: SurveyFieldType, key: string): SurveyFieldDefinition {
  const common = { key, label: defaultLabel(type), required: false };
  switch (type) {
    case 'SHORT_TEXT':
    case 'LONG_TEXT':
    case 'EMAIL':
    case 'PHONE':
    case 'DATE':
      return { type, ...common } as SurveyFieldDefinition;
    case 'NUMBER':
      return { type, ...common } as SurveyFieldDefinition;
    case 'SINGLE_SELECT':
    case 'MULTI_SELECT':
      return {
        type,
        ...common,
        options: [
          { label: 'Option 1', value: 'option_1' },
          { label: 'Option 2', value: 'option_2' },
        ],
      } as SurveyFieldDefinition;
    case 'NUMERIC_SCALE':
      return { type, ...common, min: 1, max: 5, step: 1 } as SurveyFieldDefinition;
    case 'LIKERT':
      return {
        type,
        ...common,
        options: [
          { label: 'Strongly disagree', value: 1 },
          { label: 'Disagree', value: 2 },
          { label: 'Neutral', value: 3 },
          { label: 'Agree', value: 4 },
          { label: 'Strongly agree', value: 5 },
        ],
      } as SurveyFieldDefinition;
    case 'NPS':
      return { type, ...common } as SurveyFieldDefinition;
    case 'CONSENT_CHECKBOX':
      return { type, key, label: 'I agree', required: true } as SurveyFieldDefinition;
  }
}

function defaultLabel(type: SurveyFieldType): string {
  const labels: Record<SurveyFieldType, string> = {
    SHORT_TEXT: 'Short text',
    LONG_TEXT: 'Long text',
    EMAIL: 'Email',
    PHONE: 'Phone',
    NUMBER: 'Number',
    DATE: 'Date',
    SINGLE_SELECT: 'Choose one',
    MULTI_SELECT: 'Choose any',
    NUMERIC_SCALE: 'Rate from 1 to 5',
    LIKERT: 'How much do you agree?',
    NPS: 'How likely are you to recommend us?',
    CONSENT_CHECKBOX: 'I agree',
  };
  return labels[type];
}

// ─── publish-readiness (client mirror of the flow validator; server is authoritative) ──

export interface SurveyReadinessIssue {
  message: string;
  page_id?: string;
}

const OPERATOR_LABELS: Record<SurveyBranchOperator, string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  gte: '≥',
  lte: '≤',
};

export { OPERATOR_LABELS };

/**
 * Lightweight, instant readiness signal for the builder UI. NOT authoritative —
 * the publish modal fetches GET /surveys/:id/validate for the real verdict. This
 * just lets the Publish button reflect obvious blockers without a round-trip.
 */
export function checkSurveyReadiness(state: SurveyBuilderState): SurveyReadinessIssue[] {
  const issues: SurveyReadinessIssue[] = [];
  if (state.pages.length === 0) {
    issues.push({ message: 'Add at least one page' });
    return issues;
  }
  const seen = new Set<string>();
  for (const page of state.pages) {
    if (page.fields.length === 0) {
      issues.push({ message: `Page "${page.label}" has no fields`, page_id: page.id });
    }
    for (const f of page.fields) {
      if (seen.has(f.key)) {
        issues.push({ message: `Duplicate field key "${f.key}"`, page_id: page.id });
      }
      seen.add(f.key);
    }
  }
  return issues;
}
